import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import './Vehicles.css';

const emptyForm = {
  clientId: '',
  carNumber: '',
  vin: '',
  make: '',
  body: '',
  model: '',
  engine: '',
  cc: '',
  grade: '',
  drive: '',
  year: '',
  transmission: '',
  fuel: '',
};

function Vehicles() {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEditMode = Boolean(id);
  const [form, setForm] = useState(emptyForm);
  const [status, setStatus] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [extractingPlate, setExtractingPlate] = useState(false);
  const [pageLoading, setPageLoading] = useState(false);
  const [clients, setClients] = useState([]);

  const token = localStorage.getItem('token');
  const auth = useMemo(() => ({ headers: { Authorization: `Bearer ${token}` } }), [token]);

  useEffect(() => {
    const loadPageData = async () => {
      setPageLoading(true);
      setError('');

      try {
        const requests = [
          axios.get('/api/clients', { ...auth, params: { pageSize: 500 } }),
        ];

        if (isEditMode) {
          requests.push(axios.get(`/api/vehicles/${id}`, auth));
        }

        const [clientsResponse, vehicleResponse] = await Promise.all(requests);
        setClients(clientsResponse.data.clients || []);

        if (vehicleResponse?.data?.vehicle) {
          const vehicle = vehicleResponse.data.vehicle;
          setForm({
            clientId: vehicle.ClientId ? String(vehicle.ClientId) : '',
            carNumber: vehicle.CarNumber || '',
            vin: vehicle.VINNumber || '',
            make: vehicle.Make || '',
            body: vehicle.Body || '',
            model: vehicle.Model || '',
            engine: vehicle.Engine || '',
            cc: vehicle.CC ? String(vehicle.CC) : '',
            grade: vehicle.Grade || '',
            drive: vehicle.Drive || '',
            year: vehicle.Year ? String(vehicle.Year) : '',
            transmission: vehicle.Transmission || '',
            fuel: vehicle.Fuel || '',
          });
        } else if (!isEditMode) {
          setForm(emptyForm);
        }
      } catch (requestError) {
        setError(requestError.response?.data?.message || `Failed to load ${isEditMode ? 'vehicle details' : 'clients'}.`);
      } finally {
        setPageLoading(false);
      }
    };

    loadPageData();
  }, [auth, id, isEditMode]);

  const handleChange = (field) => (event) => {
    setForm((prev) => ({
      ...prev,
      [field]: event.target.value,
    }));
  };

  const handleExtractPlate = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    setExtractingPlate(true);
    setStatus('');
    setError('');

    const formData = new FormData();
    formData.append('car', file);

    try {
      const response = await axios.post('/api/vehicles/extract-plate', formData, {
        headers: auth.headers,
      });

      const carNumber = typeof response.data === 'string'
        ? response.data
        : (response.data.english_plate || response.data.raw_text || response.data.plate || response.data.carNumber || response.data.text || '');

      if (carNumber && typeof carNumber === 'string') {
        setForm((prev) => ({ ...prev, carNumber: carNumber.trim() }));
        setStatus('Car number extracted successfully.');
      } else {
        setError('Unexpected API response format. Could not extract car number.');
      }
    } catch (requestError) {
      setError(`Failed to extract plate: ${requestError.response?.data?.message || requestError.message}`);
    } finally {
      setExtractingPlate(false);
      event.target.value = '';
    }
  };

  const handleFetchVin = async () => {
    setStatus('');
    setError('');

    if (!form.vin.trim()) {
      setError('Enter a VIN or chassis number first.');
      return;
    }

    setLoading(true);
    try {
      const response = await axios.get(`/api/vehicles/fetch-vin/${encodeURIComponent(form.vin.trim())}`, {
        headers: auth.headers,
      });

      const data = response.data?.data || {};
      const hasData = ['make', 'body', 'model', 'engine', 'cc', 'grade', 'drive', 'year', 'transmission', 'fuel']
        .some((key) => data[key]);

      setForm((prev) => ({
        ...prev,
        make: data.make || prev.make,
        body: data.body || prev.body,
        model: data.model || prev.model,
        engine: data.engine || prev.engine,
        cc: data.cc || data.CC || prev.cc,
        grade: data.grade || prev.grade,
        drive: data.drive || prev.drive,
        year: data.year || prev.year,
        transmission: data.transmission || prev.transmission,
        fuel: data.fuel || prev.fuel,
      }));

      if (hasData) {
        setStatus('Vehicle details loaded. Please verify values before saving.');
      } else {
        setStatus('No VIN data was returned. Please enter details manually.');
      }
    } catch (fetchError) {
      setError('Unable to fetch VIN data. Please enter details manually.');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (event) => {
    if (event) event.preventDefault();
    setStatus('');
    setError('');

    const clientName = clients.find((client) => String(client.ClientId) === String(form.clientId))?.ClientName || '';
    const confirmMessage = `${isEditMode ? 'Please confirm the updated vehicle details' : 'Please confirm vehicle details before saving'}:\n\n`
      + `Client: ${clientName}\n`
      + `Car number: ${form.carNumber}\n`
      + `VIN / Chassis: ${form.vin}\n`
      + `Make: ${form.make}\n`
      + `Model: ${form.model}\n`
      + `Body: ${form.body}\n`
      + `Engine: ${form.engine}\n`
      + `CC: ${form.cc}\n`
      + `Grade: ${form.grade}\n`
      + `Drive: ${form.drive}\n`
      + `Year: ${form.year}\n`
      + `Transmission: ${form.transmission}\n`
      + `Fuel: ${form.fuel}`;

    if (!window.confirm(confirmMessage)) {
      return;
    }

    try {
      if (isEditMode) {
        await axios.put(`/api/vehicles/${id}`, form, auth);
        setStatus('Vehicle updated successfully. Returning to vehicle list...');
      } else {
        await axios.post('/api/vehicles', form, auth);
        setStatus('New vehicle saved successfully. Returning to vehicle list...');
        setForm(emptyForm);
      }

      setTimeout(() => navigate('/vehicles'), 1500);
    } catch (saveError) {
      setError(saveError.response?.data?.message || `Failed to ${isEditMode ? 'update' : 'save'} vehicle.`);
    }
  };

  const isFieldEmpty = (value) => !value || value.trim() === '';

  return (
    <div className="vehicle-page">
      <div className="vehicle-header">
        <button className="back-link" onClick={() => navigate('/vehicles')}>Back to Vehicles</button>
      </div>

      <div className="vehicle-card">
        <h2>{isEditMode ? 'Edit Vehicle' : 'New Vehicle'}</h2>
        <p className="vehicle-note">
          {isEditMode
            ? 'Update vehicle details below, then save the changes back to the vehicle list.'
            : 'Enter vehicle details below. Fetch VIN data and then edit any fields before saving.'}
        </p>

        {pageLoading ? (
          <div className="vehicle-loading">Loading vehicle data...</div>
        ) : (
          <form className="vehicle-form" onSubmit={handleSubmit}>
            <div className="form-row">
              <label>Client</label>
              <select
                value={form.clientId}
                onChange={handleChange('clientId')}
                className={isFieldEmpty(form.clientId) ? 'empty-field' : ''}
                required
              >
                <option value="">Select client</option>
                {clients.map((client) => (
                  <option key={client.ClientId} value={client.ClientId}>
                    {client.ClientName} {client.Phone ? `- ${client.Phone}` : ''}
                  </option>
                ))}
              </select>
            </div>

            <div className="form-row vin-row">
              <div className="vin-field">
                <label>Car number</label>
                <input
                  value={form.carNumber}
                  onChange={handleChange('carNumber')}
                  placeholder="Enter car number"
                  className={isFieldEmpty(form.carNumber) ? 'empty-field' : ''}
                />
              </div>
              <input
                type="file"
                id="carImage"
                accept="image/jpeg, image/jpg"
                style={{ display: 'none' }}
                onChange={handleExtractPlate}
              />
              <button
                type="button"
                className="vin-button"
                onClick={() => document.getElementById('carImage').click()}
                disabled={extractingPlate}
              >
                {extractingPlate ? 'Extracting...' : 'Upload Plate'}
              </button>
            </div>

            <div className="form-row vin-row">
              <div className="vin-field">
                <label>VIN / Chassis Number</label>
                <input
                  value={form.vin}
                  onChange={handleChange('vin')}
                  placeholder="Enter VIN or chassis"
                  className={isFieldEmpty(form.vin) ? 'empty-field' : ''}
                />
              </div>
              <button type="button" className="vin-button" onClick={handleFetchVin} disabled={loading}>
                {loading ? 'Loading...' : 'Fetch VIN'}
              </button>
            </div>

            <div className="form-grid two-columns">
              <div className="form-row">
                <label>Make</label>
                <input
                  value={form.make}
                  onChange={handleChange('make')}
                  placeholder="KIA"
                  className={isFieldEmpty(form.make) ? 'empty-field' : ''}
                />
              </div>
              <div className="form-row">
                <label>Body</label>
                <input
                  value={form.body}
                  onChange={handleChange('body')}
                  placeholder="Sedan/Saloon"
                  className={isFieldEmpty(form.body) ? 'empty-field' : ''}
                />
              </div>
            </div>

            <div className="form-grid two-columns">
              <div className="form-row">
                <label>Model</label>
                <input
                  value={form.model}
                  onChange={handleChange('model')}
                  placeholder="Optima"
                  className={isFieldEmpty(form.model) ? 'empty-field' : ''}
                />
              </div>
              <div className="form-row">
                <label>Engine</label>
                <input
                  value={form.engine}
                  onChange={handleChange('engine')}
                  placeholder="2.0L I4 Hybrid"
                  className={isFieldEmpty(form.engine) ? 'empty-field' : ''}
                />
              </div>
            </div>

            <div className="form-grid two-columns">
              <div className="form-row">
                <label>CC</label>
                <input
                  value={form.cc}
                  onChange={handleChange('cc')}
                  placeholder="2000"
                  inputMode="numeric"
                  className={isFieldEmpty(form.cc) ? 'empty-field' : ''}
                />
              </div>
              <div className="form-row">
                <label>Grade</label>
                <input
                  value={form.grade}
                  onChange={handleChange('grade')}
                  placeholder="Hybrid EX"
                  className={isFieldEmpty(form.grade) ? 'empty-field' : ''}
                />
              </div>
            </div>

            <div className="form-grid two-columns">
              <div className="form-row">
                <label>Drive</label>
                <input
                  value={form.drive}
                  onChange={handleChange('drive')}
                  placeholder="FWD"
                  className={isFieldEmpty(form.drive) ? 'empty-field' : ''}
                />
              </div>
              <div className="form-row">
                <label>Year</label>
                <input
                  value={form.year}
                  onChange={handleChange('year')}
                  placeholder="2017"
                  className={isFieldEmpty(form.year) ? 'empty-field' : ''}
                />
              </div>
            </div>

            <div className="form-grid two-columns">
              <div className="form-row">
                <label>Transmission</label>
                <input
                  value={form.transmission}
                  onChange={handleChange('transmission')}
                  placeholder="6-Speed Automatic"
                  className={isFieldEmpty(form.transmission) ? 'empty-field' : ''}
                />
              </div>
              <div className="form-row">
                <label>Fuel</label>
                <input
                  value={form.fuel}
                  onChange={handleChange('fuel')}
                  placeholder="Hybrid"
                  className={isFieldEmpty(form.fuel) ? 'empty-field' : ''}
                />
              </div>
            </div>

            {error && <div className="form-error">{error}</div>}
            {status && <div className="form-success">{status}</div>}

            <button type="submit" className="save-btn">{isEditMode ? 'Update Vehicle' : 'Save Vehicle'}</button>
          </form>
        )}
      </div>
    </div>
  );
}

export default Vehicles;
